import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';

import { Button } from 'components/Button';

import { ReactComponent as CalendarIcon } from './assets/calendar.svg';

import {
  StyledInputWrapper,
  StyledDatePicker,
  StyledDropdownContent,
  StyledInput,
  StyledDropdownFooter,
} from './styled';
import CleanInputIcon from 'components/Form/shared/CleanInputIcon';
import Conditional from 'components/Conditional';
import { StyledError } from '../FormGroup/styled';

const initialState = {
  date: new Date(),
  formattedISO: '',
};

const parseDateStr = val => {
  if (!val) return initialState.date;
  try {
    return val instanceof Date ? val : parseISO(val);
  } catch {
    return initialState.date;
  }
};

const DatePicker = props => {
  const {
    size,
    value,
    placeholder,
    className = '',
    onChange,
    error,
    errormessage,
    disabled,
    'data-cy': dataCy,
    width = 'auto',
    usePortal = false,
  } = props;
  const ref = React.createRef();
  const portalRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState({});
  const [formattedValue, setFormattedValue] = useState(() => {
    if (!value) return '';
    try {
      return format(parseDateStr(value), 'dd/MM/yyyy');
    } catch {
      return '';
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState(() => parseDateStr(value));

  // Track last value processed so onChange is not called on mount spuriously
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (lastValueRef.current === value) return;
    lastValueRef.current = value;

    try {
      if (!value) {
        setDate(new Date());
        setFormattedValue('');
        onChange && onChange('');
      } else {
        handleChange(value);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error({ error });
    }
    // eslint-disable-next-line
  }, [value]);

  const toggle = () => {
    if (!isOpen && usePortal && ref?.current) {
      const rect = ref.current.getBoundingClientRect();
      setPortalStyle({ top: rect.bottom + 2, left: rect.left });
    }
    setIsOpen(!isOpen);
  };

  const handleChange = date => {
    const dateObj = date instanceof Date ? date : parseISO(date);
    const formattedValue = format(dateObj, 'dd/MM/yyyy');

    setFormattedValue(formattedValue);
    setDate(dateObj);
    onChange && onChange(date);
  };

  const handleClearDate = () => {
    setDate(initialState.date);
    setFormattedValue('');

    onChange && onChange('');
  };

  const handleClickOutside = useCallback(
    e => {
      if (ref?.current && ref.current.contains(e.target)) return;
      if (portalRef.current && portalRef.current.contains(e.target)) return;
      setIsOpen(false);
    },
    [ref],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClickOutside, ref]);

  useEffect(() => {
    if (!isOpen || !usePortal) return;
    const close = () => setIsOpen(false);
    document.addEventListener('scroll', close, true);
    return () => document.removeEventListener('scroll', close, true);
  }, [isOpen, usePortal]);

  const dropdownInner = (
    <>
      <StyledDatePicker locale={pt} onChange={handleChange} date={date} dateDisplayFormat="dd/MM/yyyy" />
      <StyledDropdownFooter>
        <Button type="button" onClick={() => setIsOpen(false)}>
          Aplicar
        </Button>
      </StyledDropdownFooter>
    </>
  );

  return (
    <StyledInputWrapper className={className} ref={ref} width={width}>
      <CalendarIcon width={13} height={13} style={{ flexShrink: 0 }} />
      <StyledInput
        readOnly
        error={error}
        type="text"
        data-cy={dataCy}
        size={size}
        placeholder={placeholder}
        value={formattedValue || ''}
        onClick={toggle}
        disabled={disabled}
      />
      <Conditional when={formattedValue !== ''}>
        <CleanInputIcon size={size} onClick={handleClearDate} />
      </Conditional>
      {usePortal ? (
        isOpen &&
        ReactDOM.createPortal(
          <div ref={portalRef} style={{ position: 'fixed', ...portalStyle, zIndex: 9999 }}>
            <StyledDropdownContent isOpen style={{ position: 'relative', top: 0 }}>
              {dropdownInner}
            </StyledDropdownContent>
          </div>,
          document.body,
        )
      ) : (
        <StyledDropdownContent isOpen={isOpen}>{dropdownInner}</StyledDropdownContent>
      )}
      <StyledError>
        <p>{error && errormessage}</p>
      </StyledError>
    </StyledInputWrapper>
  );
};

DatePicker.prototype = {
  size: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
};

export { DatePicker };
